import React from 'react';

interface AtaviGuideProps {
  onBack: () => void;
}

const TREE_DATA = [
  { idx: 5, name: 'Amaranth',    emoji: '🌿', seedCost: 10,  foodHr: 1,  maturityHr: 4,   logs: 20, removeRefund: 9.5  },
  { idx: 7, name: 'Vetiver',     emoji: '🌾', seedCost: 20,  foodHr: 2,  maturityHr: 8,   logs: 15, removeRefund: 19   },
  { idx: 4, name: 'Prickly Pear',emoji: '🌵', seedCost: 35,  foodHr: 3,  maturityHr: 16,  logs: 12, removeRefund: 33.25},
  { idx: 3, name: 'Moringa',     emoji: '🌳', seedCost: 50,  foodHr: 5,  maturityHr: 24,  logs: 9,  removeRefund: 47.5 },
  { idx: 8, name: 'Jujube',      emoji: '🫐', seedCost: 70,  foodHr: 7,  maturityHr: 36,  logs: 7,  removeRefund: 66.5 },
  { idx: 2, name: 'Acacia',      emoji: '🌲', seedCost: 90,  foodHr: 9,  maturityHr: 48,  logs: 5,  removeRefund: 85.5 },
  { idx: 1, name: 'Mesquite',    emoji: '🌴', seedCost: 120, foodHr: 11, maturityHr: 72,  logs: 4,  removeRefund: 114  },
  { idx: 6, name: 'Neem',        emoji: '🌿', seedCost: 160, foodHr: 14, maturityHr: 120, logs: 3,  removeRefund: 152  },
];

export default function AtaviGuide({ onBack }: AtaviGuideProps) {
  return (
    <div className="min-h-screen bg-slate-900 text-white" style={{ fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* Header */}
      <div className="sticky top-0 z-40 backdrop-blur-md border-b border-slate-800/60" style={{ background: 'rgba(2,6,23,0.95)' }}>
        <div className="w-full px-6 py-3 flex items-center gap-3">
          <button onClick={onBack} className="text-slate-400 hover:text-white transition-colors p-1">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
              <path d="M19 12H5M12 5l-7 7 7 7"/>
            </svg>
          </button>
          <div>
            <h1 className="text-white font-black text-base leading-tight tracking-widest">ATAVI</h1>
            <p className="text-emerald-500/70 text-xs">Forest Guide · Trees · Logs · Food</p>
          </div>
        </div>
      </div>

      <div className="w-full px-6 py-6 flex flex-col gap-6">

        {/* Hero — full width */}
        <div
          className="rounded-2xl border border-emerald-500/30 overflow-hidden"
          style={{ background: 'linear-gradient(135deg, rgba(6,78,59,0.6) 0%, rgba(2,6,23,0.98) 60%)' }}
        >
          <div className="px-8 py-7 flex items-center gap-6">
            <div
              className="w-16 h-16 rounded-2xl flex items-center justify-center text-4xl flex-shrink-0"
              style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}
            >
              🌳
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-[0.25em] text-emerald-400/70 mb-1">Samatvam Forest System</p>
              <h2 className="text-white font-black text-4xl tracking-widest">ATAVI</h2>
              <p className="text-slate-400 text-sm mt-0.5">अटवी — Sanskrit for Forest</p>
            </div>
            <p className="text-slate-300 text-sm leading-relaxed" style={{ maxWidth: '45%' }}>
              Your land plots hold trees. Trees earn <span className="text-amber-400 font-bold">Food tokens</span> every hour and —
              once mature — can be harvested for <span className="text-yellow-300 font-bold">Logs</span>.
              Logs are a scarce resource used in crafting and construction within the Samatvam economy.
            </p>
          </div>
        </div>

        {/* Three-token explainer — 3 equal columns */}
        <div className="grid grid-cols-3 gap-4">
          <div className="rounded-2xl border border-green-500/20 p-6" style={{ background: 'rgba(6,78,59,0.25)' }}>
            <div className="text-3xl mb-3">🪙</div>
            <p className="text-xs font-bold uppercase tracking-widest text-green-400/70 mb-1">Bhoomi</p>
            <p className="text-white font-black text-base mb-2">Land Token</p>
            <p className="text-slate-400 text-sm leading-relaxed">
              Earned passively from each plot you own — 0.1 Bhoomi/hr per plot regardless of tree species.
              Used to buy seeds and build infrastructure.
            </p>
          </div>
          <div className="rounded-2xl border border-amber-500/20 p-6" style={{ background: 'rgba(120,53,15,0.25)' }}>
            <div className="text-3xl mb-3">🍎</div>
            <p className="text-xs font-bold uppercase tracking-widest text-amber-400/70 mb-1">Food</p>
            <p className="text-white font-black text-base mb-2">Tree Token</p>
            <p className="text-slate-400 text-sm leading-relaxed">
              Earned from trees. Each species produces Food at a fixed rate per hour.
              Expensive trees give more Food. Used in crafting infrastructure items.
            </p>
          </div>
          <div className="rounded-2xl border border-yellow-500/20 p-6" style={{ background: 'rgba(133,77,14,0.2)' }}>
            <div className="text-3xl mb-3">🪵</div>
            <p className="text-xs font-bold uppercase tracking-widest text-yellow-400/70 mb-1">Logs</p>
            <p className="text-white font-black text-base mb-2">Harvest Resource</p>
            <p className="text-slate-400 text-sm leading-relaxed">
              Obtained by harvesting a mature tree. Requires a <span className="text-amber-300 font-bold">Hand Cart</span>.
              Each species yields a fixed number of logs per harvest. Tree resets after harvest.
            </p>
          </div>
        </div>

        {/* Main two-column layout: table on left, how-to + notes on right */}
        <div className="grid grid-cols-5 gap-6">

          {/* LEFT — Tree table (3/5 width) */}
          <div className="col-span-3 flex flex-col gap-3">
            <div className="flex items-center gap-2 mb-1">
              <div className="w-8 h-8 rounded-lg flex items-center justify-center text-base flex-shrink-0" style={{ background: 'rgba(16,185,129,0.15)', border: '1px solid rgba(16,185,129,0.3)' }}>🌲</div>
              <div>
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-400/70">8 Species</p>
                <p className="text-white font-black text-sm">Tree Comparison Table</p>
              </div>
            </div>

            <div className="rounded-2xl border border-slate-700/40 overflow-hidden" style={{ background: 'rgba(15,23,42,0.9)' }}>
              {/* Table header */}
              <div className="grid gap-0 border-b border-slate-700/60 px-5 py-3 bg-slate-800/50"
                style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr' }}>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-400">Tree</div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-400 text-right">Seed Cost</div>
                <div className="text-xs font-bold uppercase tracking-widest text-amber-400/80 text-right">Food/hr</div>
                <div className="text-xs font-bold uppercase tracking-widest text-slate-400 text-right">Matures In</div>
                <div className="text-xs font-bold uppercase tracking-widest text-yellow-400/80 text-right">Logs</div>
                <div className="text-xs font-bold uppercase tracking-widest text-green-400/80 text-right">Remove</div>
              </div>

              {TREE_DATA.map((tree, i) => (
                <div
                  key={tree.idx}
                  className={`grid gap-0 px-5 py-3.5 items-center ${i < TREE_DATA.length - 1 ? 'border-b border-slate-800/60' : ''} hover:bg-white/[0.025] transition-colors`}
                  style={{ gridTemplateColumns: '2fr 1fr 1fr 1fr 1fr 1fr' }}
                >
                  <div className="flex items-center gap-3">
                    <span className="text-xl">{tree.emoji}</span>
                    <div>
                      <p className="text-white font-bold text-sm leading-tight">{tree.name}</p>
                      <p className="text-slate-600 text-xs">species #{tree.idx}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <span className="text-green-400 font-black text-sm">{tree.seedCost}</span>
                    <span className="text-slate-600 text-xs ml-1">BHM</span>
                  </div>
                  <div className="text-right">
                    <span className="text-amber-400 font-black text-sm">{tree.foodHr}</span>
                    <span className="text-slate-600 text-xs ml-1">/hr</span>
                  </div>
                  <div className="text-right">
                    <span className="text-slate-200 font-bold text-sm">
                      {tree.maturityHr < 24 ? `${tree.maturityHr}h` : `${tree.maturityHr / 24}d`}
                    </span>
                  </div>
                  <div className="text-right">
                    <span className="text-yellow-300 font-black text-sm">{tree.logs}</span>
                    <span className="text-slate-600 text-xs ml-1">🪵</span>
                  </div>
                  <div className="text-right">
                    <span className="text-green-300 font-bold text-sm">{tree.removeRefund}</span>
                    <span className="text-slate-600 text-xs ml-1">BHM</span>
                  </div>
                </div>
              ))}
            </div>
            <p className="text-slate-600 text-xs">
              Remove refund = 95% of seed cost · Harvest requires Hand Cart · Food accumulates continuously
            </p>
          </div>

          {/* RIGHT — How to get logs + remove vs harvest (2/5 width) */}
          <div className="col-span-2 flex flex-col gap-4">

            {/* How to get logs */}
            <div
              className="rounded-2xl border border-yellow-500/20 overflow-hidden flex-1"
              style={{ background: 'linear-gradient(135deg, rgba(133,77,14,0.3) 0%, rgba(15,23,42,0.95) 60%)' }}
            >
              <div className="px-5 py-3 border-b border-yellow-500/10 flex items-center gap-2">
                <span className="text-yellow-400">🪵</span>
                <span className="text-xs font-bold uppercase tracking-widest text-yellow-400/70">How to Get Logs</span>
              </div>
              <div className="p-5 flex flex-col gap-4">
                {[
                  { n: 1, title: 'Own a Hand Cart', body: <>Craft one in the <span className="text-violet-300 font-bold">Crafting Workshop</span>. One Hand Cart serves all your plots permanently.</> },
                  { n: 2, title: 'Wait for Maturity', body: <>Each species has a fixed timer — 4 hours (Amaranth) up to 5 days (Neem). A progress bar and Harvest button appear when ready.</> },
                  { n: 3, title: 'Harvest → Logs', body: <>Logs are added to your inventory and the tree resets into a fresh growth cycle. Food accumulation continues uninterrupted.</> },
                ].map(s => (
                  <div key={s.n} className="flex items-start gap-3">
                    <div className="w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5 text-amber-300"
                      style={{ background: 'rgba(245,158,11,0.15)', border: '1px solid rgba(245,158,11,0.3)' }}>{s.n}</div>
                    <div>
                      <p className="text-white font-bold text-sm">{s.title}</p>
                      <p className="text-slate-400 text-xs leading-relaxed mt-0.5">{s.body}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Remove vs Harvest */}
            <div className="grid grid-cols-2 gap-3">
              <div className="rounded-2xl border border-red-500/20 p-4" style={{ background: 'rgba(127,29,29,0.2)' }}>
                <p className="text-xs font-bold uppercase tracking-widest text-red-400/70 mb-1">Remove</p>
                <p className="text-white font-bold text-sm mb-1">Refund, no logs</p>
                <p className="text-slate-400 text-xs leading-relaxed">Anytime. <span className="text-green-400 font-bold">95%</span> seed cost back as Bhoomi. No logs. Use to switch species.</p>
              </div>
              <div className="rounded-2xl border border-emerald-500/20 p-4" style={{ background: 'rgba(6,78,59,0.2)' }}>
                <p className="text-xs font-bold uppercase tracking-widest text-emerald-400/70 mb-1">Harvest</p>
                <p className="text-white font-bold text-sm mb-1">Logs, tree resets</p>
                <p className="text-slate-400 text-xs leading-relaxed">Mature only. Needs Hand Cart. Tree restarts. No refund — you keep the tree.</p>
              </div>
            </div>

          </div>
        </div>

        {/* Rental System */}
        <div className="rounded-2xl border border-violet-500/25 overflow-hidden" style={{ background: 'linear-gradient(135deg, rgba(76,29,149,0.25) 0%, rgba(15,23,42,0.97) 60%)' }}>
          <div className="px-6 py-4 border-b border-violet-500/15 flex items-center gap-3">
            <span className="text-2xl">🤝</span>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-violet-400/70">New Feature</p>
              <p className="text-white font-black text-base">Rental System</p>
            </div>
          </div>
          <div className="p-6 grid grid-cols-3 gap-6">

            {/* How it works */}
            <div className="col-span-1 flex flex-col gap-3">
              <p className="text-xs font-bold uppercase tracking-widest text-violet-400/70 mb-1">How It Works</p>
              {[
                { n: 1, title: 'Buy a Seed (1 WAX)', body: 'Seeds are permanent assets. Buy once, use forever. After each log harvest your seed is returned to your account.' },
                { n: 2, title: 'Plant on Any Owned Plot', body: 'You can plant on your own land or any other player\'s land. Each plot holds up to 5 trees at a time.' },
                { n: 3, title: 'Earn Food, Pay Rent', body: 'Trees on rented land earn 0.8 Food/hr instead of 1.0. The 0.2 difference flows passively to the landowner.' },
              ].map(s => (
                <div key={s.n} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-lg flex items-center justify-center text-sm font-bold flex-shrink-0 mt-0.5 text-violet-300 flex-shrink-0"
                    style={{ background: 'rgba(139,92,246,0.15)', border: '1px solid rgba(139,92,246,0.3)' }}>{s.n}</div>
                  <div>
                    <p className="text-white font-bold text-sm">{s.title}</p>
                    <p className="text-slate-400 text-xs leading-relaxed mt-0.5">{s.body}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Food rate table */}
            <div className="col-span-1">
              <p className="text-xs font-bold uppercase tracking-widest text-violet-400/70 mb-3">Food Rate Breakdown</p>
              <div className="rounded-xl border border-slate-700/40 overflow-hidden" style={{ background: 'rgba(15,23,42,0.8)' }}>
                {[
                  { role: 'Owner on own land', rate: '1.0 /hr', color: '#f59e0b', bg: 'rgba(245,158,11,0.06)' },
                  { role: 'Farmer on rented land', rate: '0.8 /hr', color: '#a78bfa', bg: 'rgba(139,92,246,0.06)' },
                  { role: 'Landowner passive income', rate: '+0.2 /hr per rented tree', color: '#34d399', bg: 'rgba(52,211,153,0.06)' },
                ].map((r, i) => (
                  <div key={i} className="flex items-center justify-between px-4 py-3 border-b border-slate-800/60 last:border-0" style={{ background: r.bg }}>
                    <span className="text-slate-300 text-sm">{r.role}</span>
                    <span className="font-black text-sm" style={{ color: r.color }}>{r.rate}</span>
                  </div>
                ))}
              </div>
              <p className="text-slate-600 text-xs mt-2 leading-relaxed">
                Landowner income is automatic — no action needed. The 0.2/hr per rented tree accrues to their Food balance continuously.
              </p>
            </div>

            {/* For landowners + for farmers */}
            <div className="col-span-1 flex flex-col gap-3">
              <div className="rounded-xl border border-amber-500/20 p-4" style={{ background: 'rgba(120,53,15,0.2)' }}>
                <p className="text-xs font-bold uppercase tracking-widest text-amber-400/70 mb-2">🏜️ For Landowners</p>
                <ul className="flex flex-col gap-1.5">
                  {[
                    'Earn 0.2 Food/hr per tree planted on your land — fully passive.',
                    'A fully rented plot (5 trees) earns 1.0 Food/hr with zero effort.',
                    '100 plots × 5 trees = 100 Food/hr passive income.',
                    'Future: set your own rent price per plot.',
                  ].map((t, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-slate-400 leading-relaxed">
                      <span className="text-amber-400 flex-shrink-0 font-bold mt-0.5">›</span><span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="rounded-xl border border-violet-500/20 p-4" style={{ background: 'rgba(76,29,149,0.15)' }}>
                <p className="text-xs font-bold uppercase tracking-widest text-violet-400/70 mb-2">🌱 For Farmers (No Land)</p>
                <ul className="flex flex-col gap-1.5">
                  {[
                    'No need to buy land — buy seeds and plant anywhere.',
                    '0.8 Food/hr per tree is still a strong return on a 1 WAX seed.',
                    'Seeds return after every log harvest — one seed works forever.',
                    'Plant on well-equipped landowners\' plots for best community synergy.',
                  ].map((t, i) => (
                    <li key={i} className="flex items-start gap-1.5 text-xs text-slate-400 leading-relaxed">
                      <span className="text-violet-400 flex-shrink-0 font-bold mt-0.5">›</span><span>{t}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

          </div>
        </div>

        {/* Bottom row: Water Harvester + Tips side by side */}
        <div className="grid grid-cols-2 gap-6">

          {/* Water Harvester */}
          <div
            className="rounded-2xl border border-cyan-500/20 px-6 py-5 flex items-start gap-4"
            style={{ background: 'rgba(8,145,178,0.1)' }}
          >
            <span className="text-3xl flex-shrink-0">💧</span>
            <div>
              <p className="text-xs font-bold uppercase tracking-widest text-cyan-400/70 mb-1">Water Harvesting Machine</p>
              <p className="text-white font-bold text-base mb-1">+10% Food production on all trees</p>
              <p className="text-slate-400 text-sm leading-relaxed">
                Craft in the Crafting Workshop.
                When placed on your territory it multiplies every tree's Food rate by 1.1×.
                One machine covers all your plots.
              </p>
            </div>
          </div>

          {/* Quick tips */}
          <div className="rounded-2xl border border-slate-700/40 px-6 py-5" style={{ background: 'rgba(15,23,42,0.6)' }}>
            <p className="text-xs font-bold uppercase tracking-widest text-slate-400 mb-3">Strategy Tips</p>
            <ul className="flex flex-col gap-2">
              {[
                'Start with Amaranth — cheapest and gives logs in just 4 hours.',
                'Neem earns the most Food/hr but needs 5 days to mature — plant it early.',
                'Hand Cart is a one-time craft. Prioritise it early to unlock log harvesting.',
                'Removing wastes growth progress. Only remove if you truly want a different species.',
                'Water Harvester bonus amplifies expensive trees the most — great synergy with Neem.',
              ].map((tip, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-slate-400 leading-relaxed">
                  <span className="text-emerald-500 flex-shrink-0 mt-0.5 font-bold">›</span>
                  <span>{tip}</span>
                </li>
              ))}
            </ul>
          </div>

          {/* ── How to Install Items ── */}
          <div className="bg-slate-800/60 border border-slate-700/50 rounded-2xl p-5">
            <h3 className="text-base font-black text-white mb-4 flex items-center gap-2">
              <span className="text-lg">📦</span> How to Install Your Item
            </h3>
            <div className="flex flex-col gap-3">
              {[
                { n: '1', icon: '🗺️', title: 'Open the Atavi Forest map', desc: 'Navigate to the Atavi Forest from the main dashboard.' },
                { n: '2', icon: '🟩', title: 'Click any empty owned plot', desc: 'Your owned plots are highlighted. Click an empty one to open the action wheel.' },
                { n: '3', icon: '⚙️', title: 'Tap 💧 Water Harvester or 🛒 Hand Cart in the wheel', desc: 'The radial menu shows all available actions for that plot. Select the item you want to install.' },
                { n: '4', icon: '✅', title: 'Item activates — benefit starts immediately', desc: 'No waiting period. Your food/hr rate updates instantly once the item is placed.' },
              ].map(step => (
                <div key={step.n} className="flex items-start gap-3">
                  <div className="w-7 h-7 rounded-full bg-emerald-500/20 border border-emerald-400/30 text-emerald-300 text-xs font-black flex items-center justify-center flex-shrink-0 mt-0.5">{step.n}</div>
                  <div>
                    <p className="text-white font-semibold text-sm">{step.icon} {step.title}</p>
                    <p className="text-slate-400 text-xs mt-0.5 leading-relaxed">{step.desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

        </div>

        <div className="h-4" />
      </div>
    </div>
  );
}
